import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  getCurrentUser,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode,
  updateUserAttributes,
  fetchAuthSession,
  fetchUserAttributes,
  type AuthUser,
} from 'aws-amplify/auth';

interface AuthContextValue {
  user: AuthUser | null;
  userAttributes: Record<string, string>;
  groups: string[];
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, role: 'student' | 'parent', isMinor: boolean, firstName?: string) => Promise<void>;
  confirmSignUp: (email: string, code: string, role: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  acceptEula: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userAttributes, setUserAttributes] = useState<Record<string, string>>({});
  const [groups, setGroups] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      const [attrs, session] = await Promise.all([
        fetchUserAttributes(),
        fetchAuthSession(),
      ]);
      setUserAttributes(attrs as Record<string, string>);
      const g = session.tokens?.accessToken.payload['cognito:groups'] as string[] | undefined;
      setGroups(g ?? []);
    } catch {
      setUser(null);
      setUserAttributes({});
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadUser(); }, []);

  const signIn = async (email: string, password: string) => {
    const attempt = () => amplifySignIn({ username: email, password });

    let result;
    try {
      result = await attempt();
    } catch (err) {
      // Stale session in localStorage — sign out silently and retry once
      if (err instanceof Error && err.message.toLowerCase().includes('already a signed in user')) {
        await amplifySignOut();
        result = await attempt();
      } else {
        throw err;
      }
    }

    // Amplify v6 does NOT throw for an unconfirmed account — signIn resolves with
    // isSignedIn:false and nextStep.signInStep === 'CONFIRM_SIGN_UP'. If we ignore
    // that (as this used to), the caller navigates home, RootRedirect finds no
    // session, and bounces back to /login — an infinite loop for anyone who never
    // finished email verification. Translate the step into the same "not confirmed"
    // signal LoginPage already routes to the verify page, and send a fresh code
    // first (the original one expires after 24h).
    if (!result.isSignedIn) {
      const step = result.nextStep?.signInStep;
      if (step === 'CONFIRM_SIGN_UP') {
        await resendSignUpCode({ username: email }).catch(() => {});
        throw new Error('User is not confirmed');
      }
      throw new Error(`Additional sign-in step required: ${step ?? 'unknown'}`);
    }

    await loadUser();
  };

  const signUp = async (email: string, password: string, role: 'student' | 'parent', isMinor: boolean, firstName?: string) => {
    await amplifySignUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          'custom:is_minor': isMinor ? 'true' : 'false',
          ...(firstName ? { given_name: firstName } : {}),
        },
        // Store intended role so admin can add them to the right group after verification
        clientMetadata: { role },
      },
    });
  };

  const confirmSignUp = async (email: string, code: string, role: string) => {
    await amplifyConfirmSignUp({
      username: email,
      confirmationCode: code,
      options: { clientMetadata: { role } },
    });
  };

  const resendCode = async (email: string) => {
    await resendSignUpCode({ username: email });
  };

  const signOut = async () => {
    await amplifySignOut();
    setUser(null);
    setUserAttributes({});
    setGroups([]);
  };

  const acceptEula = async () => {
    await updateUserAttributes({ userAttributes: { 'custom:agreed_eula': 'v1' } });
    const attrs = await fetchUserAttributes();
    setUserAttributes(attrs as Record<string, string>);
  };

  const refreshUser = loadUser;

  return (
    <AuthContext.Provider value={{
      user, userAttributes, groups, isLoading,
      signIn, signUp, confirmSignUp, resendCode, signOut, acceptEula, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
